import { TypedDocumentNode, gql } from '@apollo/client';
import { Suspense } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Navigate, Outlet, useMatch } from 'react-router-dom';

import { catchAll } from '@sorare/routing';

import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { EmptyGames } from '@sorare/core/src/components/EmptyState/EmptyGames';
import { LineupsLayoutSwitch } from '@sorare/core/src/components/lineup/LineupsLayoutSwitch';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  NBA_LIVE_PRO,
  NBA_PLAY_POSTSEASON,
  NBA_PLAY_PRO,
  NBA_PLAY_PRO_COMPETITION_DETAILS_LEADERBOARDSLUG,
  NBA_PLAY_PRO_GAMES,
  NBA_PLAY_PRO_MY_LINEUPS,
} from '@sorare/core/src/constants/__generated__/routes';
import { NBA_PLAY_PRO_COMPETITION } from '@sorare/core/src/constants/routes';
import { randomizedPollInterval } from '@sorare/core/src/gql';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { useIsRemovingMyLineupsUS } from '@sorare/core/src/hooks/navigation/useIsRemovingMyLineupsUS';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';

import {
  NbaPlayProLayoutQuery,
  NbaPlayProLayoutQueryVariables,
} from './__generated__/layout.graphql';

const NBA_PLAY_PRO_LAYOUT_QUERY = gql`
  query NbaPlayProLayoutQuery {
    nbaOpenFixture {
      slug
    }
  }
` as TypedDocumentNode<NbaPlayProLayoutQuery, NbaPlayProLayoutQueryVariables>;

const POLL_INTERVAL = randomizedPollInterval(5);

const useProPlayLayoutQuery = () => {
  const query = useQuery(NBA_PLAY_PRO_LAYOUT_QUERY, {
    pollInterval: POLL_INTERVAL,
  });

  return query;
};

export const PlayProLayout = () => {
  const { formatMessage } = useIntl();
  const {
    flags: { useNbaPlayoffs = false },
  } = useFeatureFlags();
  const { data, loading } = useProPlayLayoutQuery();

  const onPlayProPage = useMatch(NBA_PLAY_PRO);
  const onCompetitionsDetails = useMatch(
    NBA_PLAY_PRO_COMPETITION_DETAILS_LEADERBOARDSLUG
  );
  const onMyLineupsPage = useMatch(catchAll(NBA_PLAY_PRO_MY_LINEUPS));
  const onPastOrLiveCompetitionPage = useMatch(
    catchAll(NBA_PLAY_PRO_COMPETITION)
  );
  const useRemoveMyLineups = useIsRemovingMyLineupsUS();
  const showLineupLayoutSwitch =
    onMyLineupsPage ||
    onCompetitionsDetails ||
    onPastOrLiveCompetitionPage ||
    (onPlayProPage && useRemoveMyLineups);

  const { nbaOpenFixture: openFixture } = data || {};

  if (useNbaPlayoffs) {
    return <Navigate to={NBA_PLAY_POSTSEASON} replace />;
  }

  if (!data || (!data && loading)) {
    return <LoadingIndicator fullHeight />;
  }

  if (!openFixture) {
    return (
      <>
        <PageHeader text={glossary.pro} />
        <EmptyGames
          {...{
            title: formatMessage({
              id: 'emptyState.upcoming.fixture.title',
              defaultMessage: 'Come back soon!',
            }),
            message: formatMessage({
              id: 'emptyState.upcoming.fixture.message',
              defaultMessage: 'The next Game Week is not ready to open yet',
            }),
            ctaProps: {
              to: NBA_LIVE_PRO,
              children: formatMessage({
                id: 'emptyState.upcoming.fixture.cta',
                defaultMessage: 'Go to Live',
              }),
            },
          }}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader text={glossary.pro} />
      <ContentWithStickyHeader
        header={
          <FullWidthContainer.NoPadding>
            <Vertical gap={0}>
              <TabBar fullWidth variant="flat">
                <Tab
                  to={NBA_PLAY_PRO}
                  matches={[
                    NBA_PLAY_PRO,
                    catchAll(NBA_PLAY_PRO_COMPETITION_DETAILS_LEADERBOARDSLUG),
                    catchAll(NBA_PLAY_PRO_COMPETITION),
                  ]}
                >
                  <FormattedMessage {...navLabels.competitions} />
                </Tab>
                <Tab
                  to={NBA_PLAY_PRO_GAMES}
                  matches={[catchAll(NBA_PLAY_PRO_GAMES)]}
                >
                  <FormattedMessage
                    {...(useRemoveMyLineups
                      ? glossary.games
                      : navLabels.schedule)}
                  />
                </Tab>
                {!useRemoveMyLineups && (
                  <Tab
                    to={NBA_PLAY_PRO_MY_LINEUPS}
                    matches={[catchAll(NBA_PLAY_PRO_MY_LINEUPS)]}
                  >
                    <FormattedMessage {...navLabels.myLineups} />
                  </Tab>
                )}
                {showLineupLayoutSwitch && <LineupsLayoutSwitch />}
              </TabBar>
            </Vertical>
          </FullWidthContainer.NoPadding>
        }
      >
        <Suspense fallback={<LoadingIndicator fullHeight />}>
          <Outlet />
        </Suspense>
      </ContentWithStickyHeader>
    </>
  );
};

export default PlayProLayout;
